import { List, Record } from 'immutable';
import logger from '~/utils/logger/logger';
import FilterDefinitionTypeConstants from '~/constants/filter-definition-type-constants';
import type { SavedFilterAPIResponse } from '../types/api-reponse-types';
import type {
  SavableFilterTypes,
  ValueTypes,
} from '../types/filter-value-types';
import type { FilterDefinitionValueTypes } from '../reducer/MachineFilterStateRecord';
import * as filterUtils from '../utils/filter-utils';
import type { FilterDefinitionType } from './definitions';
import type { RangeType } from './filter-values/RangeFilterRecord';
import {
  FreeTextFilterRecord,
  RangeFilterRecord,
  SelectFilterRecord,
  TagFilterRecord,
} from './filter-values';
import FilterDefinitionValueRecord from './FilterDefinitionValueRecord';

const keys: Fields = {
  filters: List(),
};

export interface Fields {
  filters: List<SavableFilterTypes>;
}

export default class FilterGroupRecord extends Record<Fields>(keys) {
  constructor(values?: SavedFilterAPIResponse | Partial<Fields>) {
    super(values as any);
  }

  private getFilterIndex(filterName): number {
    return this.filters.findIndex((filter) => filter.getName() === filterName);
  }

  addFilterValue(
    filterDefinition: FilterDefinitionType,
    newFilterValue: FilterDefinitionValueTypes
  ): this {
    const presentFilterIndex = this.getFilterIndex(filterDefinition.getName());

    if (presentFilterIndex >= 0) {
      return this.updateIn(
        ['filters', presentFilterIndex],
        (filter: SavableFilterTypes) => filter.add(newFilterValue)
      );
    }

    let newFilterRecord: SavableFilterTypes | undefined;
    switch (filterDefinition.getType()) {
      case FilterDefinitionTypeConstants.MULTI_SELECT:
        newFilterRecord = new SelectFilterRecord({
          filterName: filterDefinition.getNameRecord(),
          selectedOptions: List.of(
            newFilterValue as FilterDefinitionValueRecord
          ),
        });
        break;

      case FilterDefinitionTypeConstants.MULTI_SELECT_TAGS:
        newFilterRecord = new TagFilterRecord({
          filterName: filterDefinition.getNameRecord(),
          selectedOptions: List.of(
            newFilterValue as FilterDefinitionValueRecord
          ),
        });
        break;

      case FilterDefinitionTypeConstants.RANGE:
        newFilterRecord = new RangeFilterRecord({
          filterDefinition,
          values: newFilterValue as RangeType,
        });
        break;

      case FilterDefinitionTypeConstants.TEXT:
        newFilterRecord = new FreeTextFilterRecord({
          filterDefinition,
          value: newFilterValue,
        });
        break;

      default:
        break;
    }

    if (!newFilterRecord) {
      return this;
    }

    // @ts-ignore It thinks newFilterRecord is still undefined
    return this.update('filters', (filters) => filters.push(newFilterRecord));
  }

  removeFilterValue(
    filterName: string,
    removeFilterValue?: FilterDefinitionValueRecord | 'min' | 'max' | string
  ): this {
    return filterUtils.removeFilterValue(this, filterName, removeFilterValue);
  }

  getFilterValuesByName(filterName: string): ValueTypes | undefined {
    const presentFilterIndex = this.getFilterIndex(filterName);

    if (presentFilterIndex >= 0) {
      return this.filters.get(presentFilterIndex)!.getValues();
    }
  }

  getFilterValues(): List<SavableFilterTypes> {
    return this.filters;
  }

  clearFilterValues(): this {
    return this.set('filters', List());
  }

  clearFilterValuesByName(filterName: string): this {
    const presentFilterIndex = this.getFilterIndex(filterName);

    if (presentFilterIndex >= 0) {
      return this.deleteIn(['filters', presentFilterIndex]);
    }

    logger.error('Filter not found');
    return this;
  }

  setFilterValues(filters: List<SavableFilterTypes>): this {
    return this.set('filters', filters);
  }
}
